import { z } from "zod";

// TES-700 — the access log can be read.
//
// `AccessLog` is built as the PDL access log and is written on every patient
// read. It has never had a read surface. An access log that nobody reviews
// meets the letter of the law and deters nobody — and deterrence is what
// actually protects the patient against curiosity lookups, which are the most
// common abuse in healthcare.
//
// TWO THINGS THAT LIVE IN THE SHAPE, NOT IN THE IMPLEMENTATION:
//
// 1. Staff are named, the patient is not. The reviewer needs to know WHO
//    looked; knowing who was looked at needs no identity, only the ability to
//    see that it was the same person five times. The patient is therefore an
//    opaque id. That is also what means a reviewer needs no care relationship
//    with the people they review.
//
// 2. Reading the log must not write to it. The surface mounts no
//    CareRelationshipGuard and looks up no patient — otherwise every page load
//    would have been recorded as a patient access by an admin without a care
//    relationship, and the review would have become its own largest entry.

export const accessReviewRowSchema = z.object({
  id: z.string().uuid(),
  /** Opaque — the reviewer should see the pattern, not the person. */
  patientId: z.string().uuid(),
  employeeId: z.string().uuid(),
  employeeRole: z.string(),
  purpose: z.string(),
  relationshipBasis: z.string(),
  /**
   * Was there an active care relationship when the row was written?
   *
   * This is the only column that singles anything out on its own. `false`
   * does not mean abuse — an on-call doctor opens patients she has no
   * relationship with, and that is intended — but it is the question a review
   * should start from, and it is cheap to ask precisely because the answer is
   * already in the row instead of having to be derived.
   */
  hadActiveRelationship: z.boolean(),
  route: z.string(),
  occurredAt: z.string(),
});
export type AccessReviewRow = z.infer<typeof accessReviewRowSchema>;

export const accessReviewSummarySchema = z.object({
  employeeId: z.string().uuid(),
  /** The staff member's name — the person reviewed must be recognizable. */
  employeeName: z.string().nullable(),
  employeeRole: z.string(),
  /** Number of accesses in the window. */
  accesses: z.number().int().nonnegative(),
  /** DISTINCT patients. Breadth says more than volume about curiosity. */
  patients: z.number().int().nonnegative(),
  /** How many of the accesses lacked an active care relationship. */
  withoutRelationship: z.number().int().nonnegative(),
  lastAccessAt: z.string().nullable(),
});
export type AccessReviewSummary = z.infer<typeof accessReviewSummarySchema>;

export const accessReviewWindowSchema = z.object({
  /** The window's length in days. */
  days: z.number().int().positive().max(365),
  from: z.string(),
  to: z.string(),
  rows: z.array(accessReviewSummarySchema),
  /** The total, so the reviewer sees if the selection is the whole picture. */
  totalAccesses: z.number().int().nonnegative(),
  totalWithoutRelationship: z.number().int().nonnegative(),
});
export type AccessReviewWindow = z.infer<typeof accessReviewWindowSchema>;

/** The window the review normally covers. One month, per the routine. */
export const ACCESS_REVIEW_DEFAULT_DAYS = 30;

/**
 * Does this row stand out enough to look at?
 *
 * Deliberately blunt and deliberately in the contract: admin and API must hold
 * the same threshold, and whoever changes it should see both change. This is
 * a flag in the summary, not an alarm — a flag that says "look here" is
 * useful, one that says "this is wrong" would be wrong about as often as right.
 * The alarms (TES-700, below) are separate and narrower: they leave queue
 * reads out, which this flag still counts.
 */
export function standsOut(s: AccessReviewSummary): boolean {
  return s.withoutRelationship > 0;
}

// TES-700 — alarms on the anomalous, not on everything.
//
// The ticket names exactly two patterns, and these are the only two:
//
// 1. `no_relationship` — a staff member opened a patient they had no care
//    relationship with. `hadActiveRelationship: false` on a row written by the
//    CareRelationshipGuard, i.e. the relationship was self-asserted by the act
//    of opening. Queue listings (`relationshipBasis: "clinic"`) also write
//    `false` — one row per patient in the queue — and are excluded, because
//    reading the shared work queue is the job, not a deviation.
// 2. `volume` — a staff member opened unusually many distinct patients in a
//    short time. Again only guard rows: a queue listing of thirty patients is
//    thirty rows written in the same millisecond and would trip any threshold.
//
// One alarm per (kind, staff member, Stockholm calendar day). The same person
// opening five unrelated patients on a Tuesday is one thing to look at, not
// five, and a reviewer who gets five mails about the same afternoon stops
// reading the sixth.
//
// Still not a verdict. An on-call doctor stepping in for a colleague trips
// `no_relationship`, and that is intended — the alarm says "someone should
// look at this", and acknowledging it is the record that someone did.

// TES-1003 (Ö-261, Emelie 2026-09-29) — what counts as "without a basis".
//
// "Larma vid den första unika patienten per användare och kalenderdygn"
// "som öppnas utan registrerad vårdrelation eller annan dokumenterad åtkomstgrund."
//
// A row alarms only when the guard had to improvise the relationship: no
// active relationship beforehand AND the basis it wrote is `self_asserted`.
// Every other basis is a documented ground, even on the row that created it —
// `meeting` (the booked doctor), `clinical_action` (the responsible doctor
// after a handover), `review_requested` (the patient handed something in),
// `reinstated` (the doctor's time-limited, reasoned re-access), `coaching`
// (TES-1123: a coach opening a patient whose subscription is trialing or
// active, which is what puts the coach in her care), `support`
// (TES-1040: an admin's reasoned support access, bounded to the Stockholm
// day — the exception Ö-261 names) — and `clinic` is a queue listing, which
// is not opening her record.
//
// Repeated openings dedupe by construction: the first open self-asserts a
// relationship, so every later open of the same patient has one.
export const ACCESS_ALARM_UNDOCUMENTED_BASES = ["self_asserted"] as const;

export function isUndocumentedAccess(row: {
  relationshipBasis: string;
  hadActiveRelationship: boolean;
}): boolean {
  return (
    !row.hadActiveRelationship &&
    (ACCESS_ALARM_UNDOCUMENTED_BASES as readonly string[]).includes(
      row.relationshipBasis,
    )
  );
}

// `volume` stays in the enum so alarms raised before TES-1003 still parse and
// render; nothing raises a new one while ACCESS_ALARM_VOLUME_ENABLED is off.
export const ACCESS_ALARM_KINDS = ["no_relationship", "volume"] as const;
export const accessAlarmKindSchema = z.enum(ACCESS_ALARM_KINDS);
export type AccessAlarmKind = z.infer<typeof accessAlarmKindSchema>;

/**
 * `volume`: distinct patients within the sliding window below that raise an
 * alarm. ASSUMPTION (TES-700, not set by the ticket): 20 distinct patients in
 * 60 minutes. A doctor preparing a full day of consults opens roughly a dozen;
 * twenty in an hour is browsing. Change here — admin and API read the same
 * value.
 */
export const ACCESS_ALARM_VOLUME_DISTINCT_PATIENTS = 20;
/**
 * TES-1003 (Ö-262, Emelie 2026-09-29): the volume alarm is OFF —
 * "Ett separat larm för exempelvis 20 patienter på 60 minuter ingår inte i MVP."
 * "Vi saknar verklig användningsdata för ett meningsfullt gränsvärde."
 * Every access is still in the access log. A future volume alarm needs a threshold taken
 * from observed work patterns, and must never lock a user out without a
 * separately decided incident rule.
 */
export const ACCESS_ALARM_VOLUME_ENABLED = false;
export const ACCESS_ALARM_VOLUME_WINDOW_MINUTES = 60;

/**
 * `no_relationship`: distinct patients opened without a care relationship in
 * one day that raise an alarm. 1 is the ticket read literally: a single
 * patient opened without a relationship is the anomaly it names. Raise it if
 * the lane turns out to be dominated by legitimate cover.
 */
export const ACCESS_ALARM_NO_RELATIONSHIP_MIN_PATIENTS = 1;

export const accessAlarmSchema = z.object({
  id: z.string().uuid(),
  kind: accessAlarmKindSchema,
  employeeId: z.string().uuid(),
  employeeName: z.string().nullable(),
  employeeRole: z.string(),
  /** Stockholm calendar day the alarm covers, YYYY-MM-DD. */
  day: z.string(),
  /**
   * Distinct patients behind the alarm: for `no_relationship` the day's
   * patients opened without a relationship, for `volume` the peak count in
   * one window.
   */
  patientCount: z.number().int().nonnegative(),
  /** Access-log rows behind the same count. */
  accessCount: z.number().int().nonnegative(),
  /** First and last access behind the count — the span to open in the log. */
  firstAccessAt: z.string(),
  lastAccessAt: z.string(),
  detectedAt: z.string(),
  acknowledgedAt: z.string().nullable(),
  acknowledgedByEmployeeId: z.string().nullable(),
});
export type AccessAlarm = z.infer<typeof accessAlarmSchema>;
